import React, {useEffect, useMemo, useState} from "react";
import {useApolloClient, useQuery} from "@apollo/client";
import {DateTime} from "luxon";
import NavBar from "../../components/common/NavBar";
import PaginatedFlatTable from "../../components/pagination/PaginatedFlatTable";
import useActiveProfile from "../../components/common/hooks/useActiveProfile";
import useThemeColors from "../../components/organization/portal/useThemeColors";
import useTranslation from "../../i18n/useTranslation";
import {alphaFromHex} from "../../util/colors";
import useActiveProfileOrgProOrElite from "../../components/common/hooks/useActiveProfileOrgProOrElite";
import UpgradeNotice, {UpgradeNoticeLink} from "../../components/common/UpgradeNotice";
import {getLocalizedGraphQLError} from "../../util/graphqlError";
import CreateOrganizationUserModal from "../../components/organization/CreateOrganizationUserModal";
import {
    GET_LEAGUE_PLAYERS,
    GET_PLAYER_ORGANIZATIONS,
} from "../../components/players/queries/GetLeaguePlayers";

const ROLE_COACH = 2;
const ROLE_PLAYER = 3;

function resolveRoleLabel(role, t) {
    if (role === "coach" || Number(role) === ROLE_COACH) return t("playersPage.roles.coach");
    if (role === "player" || Number(role) === ROLE_PLAYER) return t("playersPage.roles.player");
    return t("playersPage.noValue");
}

function resolveGenderLabel(gender, t) {
    if (!gender) return t("playersPage.noValue");
    return t(`playersPage.gender.${gender}`, {defaultValue: gender});
}

function formatDate(dateValue, language, emptyValue) {
    if (!dateValue) return emptyValue;
    return DateTime.fromISO(dateValue)
        .setLocale(language)
        .toLocaleString(DateTime.DATE_MED);
}

function joinNames(items, emptyValue) {
    const names = (Array.isArray(items) ? items : [])
        .map((item) => item?.name)
        .filter(Boolean);
    return names.length ? names.join(", ") : emptyValue;
}

function renderNameTags(items, emptyValue) {
    const names = (Array.isArray(items) ? items : []).map((item) => item?.name).filter(Boolean);
    if (!names.length) return <span className="kbola-player-muted">{emptyValue}</span>;
    return <div className="kbola-player-tags">{names.map((name) => <span key={name} className="kbola-player-tag">{name}</span>)}</div>;
}

export default function PlayersPage() {
    const {t, language} = useTranslation();
    const colors = useThemeColors();
    const {activeProfile} = useActiveProfile();
    const apolloClient = useApolloClient();
    const {hasAnyOrgEnterprise} = useActiveProfileOrgProOrElite();

    const [page, setPage] = useState(1);
    const [selectedOrganizationId, setSelectedOrganizationId] = useState("");
    const [selectedRoleId, setSelectedRoleId] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [showCreateUser, setShowCreateUser] = useState(false);
    const [exporting, setExporting] = useState(false);
    const pageSize = 12;

    const organizationId = selectedOrganizationId ? Number(selectedOrganizationId) : null;
    const roleIds = selectedRoleId ? [Number(selectedRoleId)] : null;
    const q = debouncedSearchTerm.trim() || null;
    const noValue = t("playersPage.noValue");

    useEffect(() => {
        const timer = window.setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setPage(1);
        }, 400);

        return () => window.clearTimeout(timer);
    }, [searchTerm]);

    const {data, loading, error, refetch} = useQuery(GET_LEAGUE_PLAYERS, {
        variables: {
            profileId: activeProfile?.id ?? 0,
            organizationId,
            roleIds,
            q,
            page,
            pageSize,
        },
        skip: !activeProfile?.id || !hasAnyOrgEnterprise,
        fetchPolicy: "cache-and-network",
    });

    const {data: organizationsData, loading: organizationsLoading} = useQuery(GET_PLAYER_ORGANIZATIONS, {
        variables: {
            profileId: activeProfile?.id ?? 0,
        },
        skip: !activeProfile?.id || !hasAnyOrgEnterprise,
        fetchPolicy: "cache-and-network",
    });

    const players = useMemo(() => {
        return Array.isArray(data?.leaguePlayers?.objects) ? data.leaguePlayers.objects : [];
    }, [data?.leaguePlayers?.objects]);

    const totalPages = useMemo(() => {
        return Number(data?.leaguePlayers?.pages) || 1;
    }, [data?.leaguePlayers?.pages]);

    const totalCount = useMemo(() => {
        return Number(data?.leaguePlayers?.totalCount) || 0;
    }, [data?.leaguePlayers?.totalCount]);

    const organizationOptions = useMemo(() => {
        const organizations = Array.isArray(organizationsData?.organizations?.objects)
            ? organizationsData.organizations.objects
            : [];

        return organizations
            .filter((organization) => Number(organization?.id))
            .map((organization) => ({
                id: Number(organization.id),
                name: organization.name || "",
                slug: organization.slug || "",
            }));
    }, [organizationsData?.organizations?.objects]);

    const resetFilters = () => {
        setSelectedOrganizationId("");
        setSelectedRoleId("");
        setSearchTerm("");
        setDebouncedSearchTerm("");
        setPage(1);
    };

    const navBarElem = useMemo(
        () => ({
            pageTitle: t("playersPage.pageTitle"),
            links: [
                {pageTitle: t("playersPage.settings"), navLink: null, active: false},
                {pageTitle: t("playersPage.pageTitle"), navLink: null, active: true},
            ],
        }),
        [t]
    );

    const headers = useMemo(
        () => [
            t("playersPage.fields.fullName"),
            t("playersPage.fields.email"),
            t("playersPage.fields.phone"),
            t("playersPage.fields.gender"),
            t("playersPage.fields.dob"),
            t("playersPage.fields.organization"),
            t("playersPage.fields.activeTeam"),
            t("playersPage.fields.role"),
        ],
        [t]
    );

    const buildExportRows = (rows) => {
        return rows.map((player) => {
            const roles = Array.isArray(player?.roles) && player.roles.length
                ? player.roles.map((role) => resolveRoleLabel(role, t)).join(" / ")
                : resolveRoleLabel(player?.roleId || player?.role, t);

            return {
                [t("playersPage.fields.fullName")]: player.fullName || "",
                [t("playersPage.fields.email")]: player.email || "",
                [t("playersPage.fields.phone")]: player.phone || "",
                [t("playersPage.fields.gender")]: player.gender ? resolveGenderLabel(player.gender, t) : "",
                [t("playersPage.fields.dob")]: player.dob
                    ? DateTime.fromISO(player.dob).toFormat("yyyy-MM-dd")
                    : "",
                [t("playersPage.fields.organization")]: joinNames(player?.organizations, ""),
                [t("playersPage.fields.activeTeam")]: joinNames(player?.activeTeams, ""),
                [t("playersPage.fields.role")]: roles === noValue ? "" : roles,
            };
        });
    };

    const handleDownloadExcel = async () => {
        if (!activeProfile?.id || exporting) return;
        if (!hasAnyOrgEnterprise) return;

        setExporting(true);
        try {
            const exportPageSize = Math.max(totalCount || players.length || 1, 1);
            const result = await apolloClient.query({
                query: GET_LEAGUE_PLAYERS,
                variables: {
                    profileId: activeProfile.id,
                    organizationId,
                    roleIds,
                    q,
                    page: 1,
                    pageSize: exportPageSize,
                },
                fetchPolicy: "network-only",
            });

            const exportRows = Array.isArray(result?.data?.leaguePlayers?.objects)
                ? result.data.leaguePlayers.objects
                : [];

            const XLSX = await import("xlsx");
            const worksheet = XLSX.utils.json_to_sheet(buildExportRows(exportRows));
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, t("playersPage.pageTitle"));

            const timestamp = DateTime.local().toFormat("yyyy-LL-dd");
            XLSX.writeFile(workbook, `players-${timestamp}.xlsx`);
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="content-wrapper">
            <NavBar navBarElem={navBarElem}/>

            <section className="content">
                <div className="container-fluid" id="kbola-players-scope">
                    {error ? (
                        <div className="alert alert-danger col-6 mx-auto text-center">{getLocalizedGraphQLError(error, t, "errors.somethingWentWrong")}</div>
                    ) : null}

                    {!hasAnyOrgEnterprise ? (
                        <UpgradeNotice
                            planLabel={t("playersPage.upgradePlanLabel")}
                            title={t("playersPage.upgradeTitle")}
                            subtitle={t("playersPage.upgradeSubtitle")}
                            ctaLabel={t("playersPage.upgradeCta")}
                            to="/subscription/plans"
                        >
                            {t("playersPage.upgradeTextPrefix")}{" "}
                            <UpgradeNoticeLink to="/subscription/plans">
                                {t("playersPage.upgradeTextLink")}
                            </UpgradeNoticeLink>
                            .
                        </UpgradeNotice>
                    ) : (
                        <>
                            <div className="row mb-3">
                                <div className="col-12">
                                    <div className="kbola-card kbola-filters-card">
                                <div className="kbola-card-head">
                                    <div className="kbola-card-head-left">
                                        <span className="kbola-card-badge">{t("playersPage.filtersBadge")}</span>
                                        <div className="kbola-card-head-text">
                                            <div className="kbola-card-title">{t("playersPage.filtersTitle")}</div>
                                            <div className="kbola-card-subtitle">
                                                {t("playersPage.filtersSubtitle")}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="kbola-card-head-right">
                                        <button
                                            type="button"
                                            className="kbola-btn kbola-btn-primary"
                                            onClick={() => setShowCreateUser(true)}
                                        >
                                            <i className="fas fa-user-plus"/>
                                            {t("organizations.createUserSubmit")}
                                        </button>
                                        <button
                                            type="button"
                                            className="kbola-btn kbola-btn-primary"
                                            onClick={handleDownloadExcel}
                                            title={t("playersPage.downloadExcel")}
                                            disabled={exporting || loading || totalCount === 0}
                                        >
                                            <i className="fas fa-file-excel"/>
                                            {exporting ? t("playersPage.downloading") : t("playersPage.download")}
                                        </button>
                                        <button
                                            type="button"
                                            className="kbola-btn kbola-btn-ghost"
                                            onClick={resetFilters}
                                            title={t("playersPage.resetFilters")}
                                            disabled={!selectedOrganizationId && !selectedRoleId && !searchTerm}
                                        >
                                            <i className="fas fa-undo"/>
                                            {t("playersPage.reset")}
                                        </button>
                                    </div>
                                </div>

                                <div className="kbola-card-body">
                                    <div className="row">
                                        <div className="col-md-4 col-12 mb-3">
                                            <label className="kbola-label">{t("playersPage.search")}</label>
                                            <input
                                                type="text"
                                                className="form-control kbola-select"
                                                value={searchTerm}
                                                onChange={(event) => {
                                                    setSearchTerm(event.target.value);
                                                }}
                                                placeholder={t("playersPage.searchPlaceholder")}
                                            />
                                        </div>

                                        <div className="col-md-4 col-12 mb-3">
                                            <label className="kbola-label">{t("playersPage.organization")}</label>
                                            <select
                                                className="form-control kbola-select"
                                                value={selectedOrganizationId}
                                                onChange={(event) => {
                                                    setSelectedOrganizationId(event.target.value);
                                                    setPage(1);
                                                }}
                                                disabled={organizationsLoading}
                                            >
                                                <option value="">{t("playersPage.allOrganizations")}</option>
                                                {organizationOptions.map((organization) => (
                                                    <option key={organization.id} value={organization.id}>
                                                        {organization.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="col-md-4 col-12 mb-3">
                                            <label className="kbola-label">{t("playersPage.profile")}</label>
                                            <select
                                                className="form-control kbola-select"
                                                value={selectedRoleId}
                                                onChange={(event) => {
                                                    setSelectedRoleId(event.target.value);
                                                    setPage(1);
                                                }}
                                            >
                                                <option value="">{t("playersPage.allProfiles")}</option>
                                                <option value={ROLE_COACH}>{t("playersPage.roles.coach")}</option>
                                                <option value={ROLE_PLAYER}>{t("playersPage.roles.player")}</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="kbola-filters-hint">
                                        <i className="fas fa-filter"/>
                                        {t("playersPage.autoRefreshHint")}
                                    </div>
                                </div>
                            </div>
                                </div>
                            </div>

                            <PaginatedFlatTable
                                title={t("playersPage.pageTitle")}
                                subtitle={t("playersPage.subtitle")}
                                page={page}
                                pages={totalPages}
                                totalCount={totalCount}
                                onPageChange={setPage}
                                items={players}
                                loading={loading}
                                headers={headers}
                                renderRow={(player) => {
                                    const organizations = joinNames(player?.organizations, noValue);
                                    const activeTeams = joinNames(player?.activeTeams, noValue);
                                    const roles = Array.isArray(player?.roles) && player.roles.length
                                        ? player.roles.map((role) => resolveRoleLabel(role, t)).join(" / ")
                                        : resolveRoleLabel(player?.roleId || player?.role, t);

                                    return (
                                        <tr key={player.profileId || player.id} className="kbola-player-row">
                                            <td>
                                                <div className="kbola-person-cell">
                                                    {player?.logoUrl ? (
                                                        <img
                                                            className="kbola-person-avatar"
                                                            src={player.logoUrl}
                                                            alt={player.fullName || t("playersPage.pageTitle")}
                                                        />
                                                    ) : (
                                                        <span className="kbola-person-avatar kbola-person-avatar-empty">
                                                            {(player?.fullName || "?").trim().charAt(0).toUpperCase()}
                                                        </span>
                                                    )}
                                                    <div className="kbola-player-identity">
                                                        <strong title={player.fullName || noValue}>{player.fullName || noValue}</strong>
                                                        <small>{player.email || noValue}</small>
                                                    </div>
                                                </div>
                                            </td>
                                            <td><span className="kbola-player-contact"><i className="fas fa-envelope" />{player.email || noValue}</span></td>
                                            <td><span className="kbola-player-contact"><i className="fas fa-phone" />{player.phone || noValue}</span></td>
                                            <td><span className="kbola-player-detail-pill"><i className="fas fa-user" />{resolveGenderLabel(player.gender, t)}</span></td>
                                            <td><span className="kbola-player-detail-pill"><i className="far fa-calendar-alt" />{formatDate(player.dob, language, noValue)}</span></td>
                                            <td>{renderNameTags(player?.organizations, organizations)}</td>
                                            <td>{renderNameTags(player?.activeTeams, activeTeams)}</td>
                                            <td className="text-right">
                                                <span className="kbola-role-pill">{roles}</span>
                                            </td>
                                        </tr>
                                    );
                                }}
                            />
                        </>
                    )}
                </div>
            </section>

            <CreateOrganizationUserModal
                show={showCreateUser}
                onClose={() => setShowCreateUser(false)}
                organizationId={selectedOrganizationId}
                organizations={organizationOptions}
                allowRoleSelection
                colors={colors}
                onCreated={() => refetch()}
            />

            <style>{`
                #kbola-players-scope .kbola-card{
                    border-radius: 18px;
                    overflow: hidden;
                    border: 1px solid rgba(0,0,0,0.08);
                    background: #fff;
                    box-shadow: 0 18px 44px rgba(0,0,0,0.10);
                }
                #kbola-players-scope .kbola-card-head{
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                    padding: 12px 14px;
                    background: linear-gradient(
                        135deg,
                        ${alphaFromHex(colors.primary, 0.50)},
                        ${alphaFromHex(colors.primary, 0.00)}
                    );
                    border-bottom: 1px solid rgba(0,0,0,0.06);
                }
                #kbola-players-scope .kbola-card-head-left{
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    min-width: 0;
                }
                #kbola-players-scope .kbola-card-head-right{
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                    gap: 10px;
                    flex-wrap: wrap;
                }
                #kbola-players-scope .kbola-card-badge{
                    font-size: 12px;
                    font-weight: 900;
                    letter-spacing: 0.03em;
                    padding: 6px 10px;
                    border-radius: 999px;
                    background: ${colors.primary};
                    border: 1px solid rgba(0,0,0,0.10);
                    color: ${colors.textOnPrimary};
                    white-space: nowrap;
                    flex: 0 0 auto;
                }
                #kbola-players-scope .kbola-card-title{
                    font-weight: 900;
                    color: #14151a;
                    font-size: 14px;
                    line-height: 1.1;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                #kbola-players-scope .kbola-card-subtitle{
                    font-weight: 700;
                    color: rgba(20,21,26,0.55);
                    font-size: 12.5px;
                    margin-top: 2px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                #kbola-players-scope .kbola-card-body{
                    padding: 14px;
                }
                #kbola-players-scope .kbola-label{
                    font-weight: 900;
                    font-size: 12.5px;
                    color: rgba(20,21,26,0.72);
                    margin-bottom: 6px;
                    display: inline-block;
                }
                #kbola-players-scope .kbola-select{
                    border-radius: 12px;
                    border: 1px solid rgba(0,0,0,0.10);
                    font-weight: 800;
                }
                #kbola-players-scope .kbola-select:focus,
                #kbola-players-scope .kbola-btn:focus{
                    outline: none;
                    box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
                    border-color: rgba(var(--kbs-primary-rgb, 0,173,255), 0.65);
                }
                #kbola-players-scope .kbola-filters-hint{
                    margin-top: 2px;
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    font-weight: 800;
                    color: rgba(20,21,26,0.60);
                    font-size: 12.5px;
                    padding: 8px 10px;
                    border-radius: 12px;
                    border: 1px solid rgba(0,0,0,0.06);
                    background: rgba(0,0,0,0.015);
                }
                #kbola-players-scope .kbola-btn{
                    border-radius: 12px;
                    padding: 10px 12px;
                    font-weight: 900;
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    border: 1px solid rgba(0,0,0,0.12);
                    background: #fff;
                    color: #14151a;
                    cursor: pointer;
                    line-height: 1;
                    white-space: nowrap;
                }
                #kbola-players-scope .kbola-btn:disabled{
                    opacity: 0.65;
                    cursor: not-allowed;
                }
                #kbola-players-scope .kbola-btn-ghost{
                    background: rgba(0,0,0,0.015);
                }
                #kbola-players-scope .kbola-btn-primary{
                    background: ${colors.primary};
                    color: ${colors.textOnPrimary};
                    border-color: rgba(0,0,0,0.10);
                }
                #kbola-players-scope .kbola-person-cell{
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    min-width: 220px;
                    max-width: 280px;
                }
                #kbola-players-scope .kbola-player-identity{
                    min-width: 0;
                    display: flex;
                    flex-direction: column;
                    gap: 3px;
                }
                #kbola-players-scope .kbola-player-identity strong{
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    color: #17202c;
                    font-size: 14px;
                    font-weight: 900;
                }
                #kbola-players-scope .kbola-player-identity small{
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    color: rgba(20,21,26,0.55);
                    font-size: 11.5px;
                    font-weight: 700;
                }
                #kbola-players-scope .kbola-player-contact{
                    display: inline-flex;
                    align-items: center;
                    gap: 7px;
                    color: rgba(20,21,26,0.72);
                    white-space: nowrap;
                }
                #kbola-players-scope .kbola-player-contact i,
                #kbola-players-scope .kbola-player-detail-pill i{
                    color: ${colors.primary};
                    font-size: 11px;
                }
                #kbola-players-scope .kbola-player-detail-pill{
                    display: inline-flex;
                    align-items: center;
                    gap: 7px;
                    padding: 6px 9px;
                    border-radius: 999px;
                    background: rgba(20,21,26,0.035);
                    border: 1px solid rgba(20,21,26,0.07);
                    color: rgba(20,21,26,0.70);
                    font-size: 12px;
                    font-weight: 800;
                    white-space: nowrap;
                }
                #kbola-players-scope .kbola-player-tags{
                    display: flex;
                    flex-wrap: wrap;
                    gap: 5px;
                    min-width: 120px;
                }
                #kbola-players-scope .kbola-player-tag{
                    display: inline-flex;
                    align-items: center;
                    max-width: 170px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    padding: 5px 8px;
                    border-radius: 8px;
                    background: ${alphaFromHex(colors.primary, 0.10)};
                    border: 1px solid ${alphaFromHex(colors.primary, 0.18)};
                    color: rgba(20,21,26,0.76);
                    font-size: 11.5px;
                    font-weight: 800;
                }
                #kbola-players-scope .kbola-player-muted{
                    color: rgba(20,21,26,0.42);
                    font-weight: 700;
                }
                #kbola-players-scope .kbola-person-avatar{
                    width: 36px;
                    height: 36px;
                    border-radius: 50%;
                    object-fit: cover;
                    flex: 0 0 auto;
                    border: 1px solid rgba(0,0,0,0.10);
                }
                #kbola-players-scope .kbola-person-avatar-empty{
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    background: ${colors.primary};
                    color: ${colors.textOnPrimary};
                    font-weight: 900;
                }
                #kbola-players-scope .kbola-role-pill{
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 999px;
                    padding: 5px 10px;
                    background: ${alphaFromHex(colors.primary, 0.14)};
                    border: 1px solid ${alphaFromHex(colors.primary, 0.24)};
                    color: rgba(20,21,26,0.82);
                    font-weight: 900;
                    white-space: nowrap;
                }
                @media (max-width: 900px){
                    #kbola-players-scope .kbola-person-cell{min-width: 190px;}
                    #kbola-players-scope .kbola-player-contact{white-space: normal;}
                }
            `}</style>
        </div>
    );
}
